import { InfoCircleFilled, LoadingOutlined } from '@ant-design/icons'
import { Button, Input } from 'antd'
import { successMessage } from 'components/BannerComponent'
import { useStoreActions, useStoreState } from 'easy-peasy'
import PropTypes from 'prop-types'
import { useEffect, useState } from 'react'
import styled from 'styled-components'
import { StyledFormWrapper } from '../styles'

const Title = styled.h2`
  color: #8b4513;
  font-size: 48px;
  margin-bottom: 2rem !important;
`

const Description = styled.p`
  color: #000;
  margin-bottom: 2rem !important;
`

const OTPInputContainer = styled.div`
  display: flex;
  gap: 8px;
  justify-content: center;
  margin-bottom: 1.5rem;
`

const StyledInput = styled(Input)`
  width: 72px !important;
  height: 72px;
  text-align: center;
  font-size: 20px;
  border-radius: 4px;
  border: 1px solid #d9d9d9;

  &:focus {
    border-color: #ff6b00;
    box-shadow: 0 0 0 2px rgba(255, 107, 0, 0.2);
  }
`

const Timer = styled.div`
  color: #666;
  margin-top: 1rem;
  font-size: 14px;
  cursor: not-allowed;
`

const ResendButton = styled.button`
  color: #000;
  margin-top: 1rem;
  font-size: 14px;
  background: none;
  border: none;
  cursor: pointer;
`

const SecurityNote = styled.div`
  color: #666;
  font-size: 12px;
  margin-top: 2rem;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 12px;
`

const MrPeriImage = styled.img`
  margin-bottom: 2rem;
`

const SecurityNoteText = styled.p`
  margin: 0;
  line-height: 1.5;
  max-width: 400px;
  color: var(--cognac);
  font-size: 12px;
  font: Kreon;
`

const ErrorMessage = styled.div`
  display: flex;
  align-items: center;
  background-color: #fdeaea;
  border-radius: 4px;
  padding: 12px;
  margin-bottom: 16px;
  border-left: 4px solid #f5222d;

  svg {
    color: #f5222d;
    margin-right: 8px;
    font-size: 16px;
  }

  color: #d4380d;
  font-size: 14px;
  font-family: Kreon, serif;
  line-height: 1.5;
`

export default function ForceUpdatePasswordOTP({ form, setPasswordStatus, setConfirmOtp }) {
  const state = useStoreState((s) => ({
    resendOtpState: s.resendOtpState,
    forceResetVerifyOtpState: s.forceResetVerifyOtpState
  }))
  const actions = useStoreActions((a) => ({
    resendOtp: a.resendOtp,
    setResendOtpState: a.setResendOtpState,
    forceResetVerifyOtp: a.forceResetVerifyOtp,
    setForceResetVerifyOtpState: a.setForceResetVerifyOtpState
  }))

  const [otp, setOtp] = useState(['', '', '', '', '', ''])
  const [timer, setTimer] = useState(60)

  useEffect(() => {
    if (timer > 0) {
      const interval = setInterval(() => {
        setTimer((prev) => prev - 1)
      }, 1000)
      return () => clearInterval(interval)
    }
  }, [timer])

  const handleChange = (value, index) => {
    if (value.length <= 1) {
      const newOtp = [...otp]
      newOtp[index] = value
      setOtp(newOtp)

      // Select next box if value is entere
      if (value && index < 5) {
        const nextInput = document.querySelector(
          `input[name="otp-${index + 1}"]`
        )
        if (nextInput) nextInput.focus()
      }
    }
  }

  const handleKeyDown = (e, index) => {
    if (e.key === 'Backspace' && otp[index] === '') {
      const prevInput = document.querySelector(
        `input[name="otp-${index - 1}"]`
      )
      if (prevInput) prevInput.focus()
    }
  }

  const handleSubmit = () => {
    const otpValue = otp.join('')
    actions.forceResetVerifyOtp({ email: form.getFieldValue('username'), otp: otpValue })
  }

  const getMaskedEmail = () => {
    if (typeof window === 'undefined') return ''

    const email = form.getFieldValue('username')

    if (!email) return ''

    return email.replace(
      /(.{2})(.*)(?=@)/g,
      (_, start, rest) => start + '*'.repeat(rest.length)
    )
  }

  const resendOTP = () => {
    if (timer === 0) {
      actions.resendOtp({ email: form.getFieldValue('username') })
      setTimer(60)
    }
  }

  useEffect(() => {
    const { status } = state.resendOtpState
    if (status === 2) {
      successMessage('OTP sent to your email')
    }
  }, [state.resendOtpState])

  useEffect(() => {
    const { status, payload } = state.forceResetVerifyOtpState
    if (status === 2) {
      successMessage('OTP verified successfully')
      setConfirmOtp(payload.confirm_otp)
      setPasswordStatus('update-pw')
    }
  }, [state.forceResetVerifyOtpState])

  return (
    <StyledFormWrapper>
      <MrPeriImage
        className="mr-peri"
        alt="MR. PERI"
        draggable="false"
        src="/images/auth-assets/mr-peri.png"
      />

      <Title>
        Time for a quick <br /> password update
      </Title>
      <Description>
        We've sent a One-Time PIN (OTP) to your email address {getMaskedEmail()}
        . Please check your inbox (or spam folder) and enter the code to
        continue.
      </Description>

      { state.forceResetVerifyOtpState.status === 3 && (
        <ErrorMessage>
          <img src="/icons/alert-iconify.png" alt="error" />
          <span>
            {state.forceResetVerifyOtpState.payload.message || 'The One-Time Password you entered is invalid. Please double-check and try again.'}
          </span>
        </ErrorMessage>
      )}

      <OTPInputContainer>
        {otp.map((digit, index) => (
          <StyledInput
            key={index}
            name={`otp-${index}`}
            value={digit}
            onChange={(e) => handleChange(e.target.value, index)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            maxLength={1}
            disabled={state.forceResetVerifyOtpState.status === 1}
            style={state.forceResetVerifyOtpState.status === 3 ? { borderColor: '#f5222d', boxShadow: '0 0 0 2px rgba(245, 34, 45, 0.2)' } : {}}
          />
        ))}
      </OTPInputContainer>

      <Button style={{ width: '100%' }} type="primary" onClick={handleSubmit} disabled={state.forceResetVerifyOtpState.status === 1 || state.resendOtpState.status === 1}>
        {state.forceResetVerifyOtpState.status === 1 || state.resendOtpState.status === 1 ? <LoadingOutlined /> : 'Next'}
      </Button>

      {timer > 0
        ? (
          <Timer>{timer}s to resend code</Timer>
          )
        : (
          <ResendButton onClick={() => resendOTP()} disabled={state.resendOtpState.status === 1}>
            Didn't get the code? <span style={{ color: 'var(--orange-peel)', textDecoration: 'underline' }}>Resend OTP</span>
            {state.resendOtpState.status === 1 && <LoadingOutlined style={{ marginLeft: '8px' }} />}
          </ResendButton>
          )}

      <SecurityNote>
        {/* <LightbulbIcon /> */}
        <SecurityNoteText>
          We're rolling out a small update to help keep your account secure.
          It's a quick step that helps us keep your information protected.
        </SecurityNoteText>
      </SecurityNote>
    </StyledFormWrapper>
  )
}

ForceUpdatePasswordOTP.propTypes = {
  form: PropTypes.object.isRequired,
  setPasswordStatus: PropTypes.func.isRequired,
  setConfirmOtp: PropTypes.func.isRequired
}
